import globeLottieAnimation from 'assets/lotties/globe.json';
import Lottie from 'lottie-react';
import { GoogleMap, useGoogleMapScriptLoader } from 'modules/shared/features/google-map';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ICamera } from '@api/models';
import MapCameraMarker from 'modules/cameras/features/MapCameraMarker/MapCameraMarker';
import { calculateMapBounds } from 'modules/cameras/helper/getMapBounds';
import CameraPolygon from '../CameraPolygon/CameraPolygon';

interface MapViewProps {
    cameras: ICamera[];
    loadingMessage: string;
    animationDuration?: number;

    selectedCamera?: ICamera | null;
    cameraAngle?: number;
    cameraRange?: number;
    cameraFOV?: number;
    onCameraSelect?: (camera: ICamera) => void;

    isEditingLocation?: boolean;
    tempMarkerPosition?: google.maps.LatLngLiteral | null;
    onPickLocation?: (pos: google.maps.LatLngLiteral) => void;
    onSelectedMarkerDragEnd?: (pos: google.maps.LatLngLiteral) => void;
    onViewChange?: (view: { center: google.maps.LatLngLiteral; zoom: number }) => void;
}

const CamerasMapView = (props: MapViewProps) => {
    const {
        cameras,
        loadingMessage,
        animationDuration = 3500,
        selectedCamera,
        cameraAngle,
        cameraRange,
        cameraFOV,
        onCameraSelect,
        isEditingLocation,
        tempMarkerPosition,
        onPickLocation,
        onSelectedMarkerDragEnd,
        onViewChange,
    } = props;

    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const { isLoaded } = useGoogleMapScriptLoader();

    useEffect(() => {
        if (!isLoaded) return;
        const t = setTimeout(() => setIsMapLoaded(true), animationDuration);
        return () => clearTimeout(t);
    }, [isLoaded, animationDuration]);

    const camerasWithLatLng = useMemo(() => {
        if (!cameras) return [];
        return cameras.filter((p) => p.location?.latitude && p.location?.longitude);
    }, [cameras]);

    const mapBounds = useMemo(() => {
        return calculateMapBounds(
            camerasWithLatLng.map((el) => el.location as { latitude: string; longitude: string })
        );
    }, [camerasWithLatLng]);

    // ✅ Freeze initial view so rerenders (due to camera updates) don't reset zoom/center
    const initialViewRef = useRef<{ center: google.maps.LatLngLiteral; zoom: number } | null>(null);

    const initialCenter = useMemo(() => {
        if (!initialViewRef.current) {
            initialViewRef.current = {
                center: {
                    lat: +mapBounds.center.latitude,
                    lng: +mapBounds.center.longitude,
                },
                zoom: mapBounds.zoom - 2,
            };
        }
        return initialViewRef.current.center;
    }, [mapBounds.center.latitude, mapBounds.center.longitude, mapBounds.zoom]);

    const initialZoom = useMemo(() => {
        if (!initialViewRef.current) {
            initialViewRef.current = {
                center: {
                    lat: +mapBounds.center.latitude,
                    lng: +mapBounds.center.longitude,
                },
                zoom: mapBounds.zoom - 2,
            };
        }
        return initialViewRef.current.zoom;
    }, [mapBounds.center.latitude, mapBounds.center.longitude, mapBounds.zoom]);

    const mapRef = useRef<google.maps.Map | null>(null);

    // store view continuously (NO setState; avoids flicker feedback-loop)
    const lastViewRef = useRef<{ center: google.maps.LatLngLiteral; zoom: number } | null>(null);

    const smoothZoom = useCallback((map: google.maps.Map, targetZoom: number) => {
        const current = map.getZoom() ?? 15;
        if (current >= targetZoom) return;

        let z = current;
        const interval = setInterval(() => {
            z += 1;
            map.setZoom(z);
            if (z >= targetZoom) clearInterval(interval);
        }, 100);
    }, []);

    const handleLoad = useCallback(
        (map: google.maps.Map) => {
            mapRef.current = map;

            // Keep last view in a ref so we can restore it after parent rerenders
            map.addListener('idle', () => {
                const center = map.getCenter();
                const zoom = map.getZoom();
                if (!center || zoom === undefined) return;

                const view = { center: center.toJSON(), zoom };

                lastViewRef.current = { center: center.toJSON(), zoom };

                onViewChange?.(view);
            });

            // click-to-pick only while editing
            map.addListener('click', (e: google.maps.MapMouseEvent) => {
                if (!e.latLng) return;
                if (isEditingLocation && onPickLocation) {
                    onPickLocation(e.latLng.toJSON());
                }
            });
        },
        [isEditingLocation, onPickLocation]
    );

    // ✅ When toggling edit mode ON, lock the view (keep zoom/center)
    useEffect(() => {
        if (!mapRef.current) return;
        if (!isEditingLocation) return;

        const view = lastViewRef.current;
        if (!view) return;

        mapRef.current.setZoom(view.zoom);
        mapRef.current.panTo(view.center);
    }, [isEditingLocation]);

    // ✅ When cameras list updates (due to onSaveCamera preview), restore user's last view
    // This prevents "zooming out" after Apply.
    useEffect(() => {
        if (!mapRef.current) return;

        const view = lastViewRef.current;
        if (!view) return;

        // if editing, we don't want any jumps
        if (isEditingLocation) return;

        // restore view after rerender
        requestAnimationFrame(() => {
            mapRef.current?.setZoom(view.zoom);
            mapRef.current?.panTo(view.center);
        });
    }, [cameras, isEditingLocation]);

    const focusCameraOnMap = useCallback(
        (camera: ICamera) => {
            if (!mapRef.current) return;

            // do not move the map while editing location
            if (isEditingLocation) {
                onCameraSelect?.(camera);
                return;
            }

            const lat = +camera.location.latitude;
            const lng = +camera.location.longitude;

            onCameraSelect?.(camera);

            mapRef.current.panTo({ lat, lng });
            smoothZoom(mapRef.current, 16);
        },
        [onCameraSelect, smoothZoom, isEditingLocation]
    );

    return (
        <>
            {isMapLoaded ? (
                <GoogleMap center={initialCenter} zoom={initialZoom} onLoad={handleLoad}>
                    {camerasWithLatLng.map((camera) => {
                        const isSelected = selectedCamera?._id === camera._id;

                        return (
                            <React.Fragment key={camera._id}>
                                <CameraPolygon
                                    camera={camera}
                                    isSelected={isSelected}
                                    selectedAngle={cameraAngle}
                                    selectedRange={cameraRange}
                                    selectedFOV={cameraFOV}
                                    onClick={() => focusCameraOnMap(camera)}
                                />

                                {isEditingLocation && selectedCamera && tempMarkerPosition && (
                                    <GoogleMap.AdvancedMarker
                                        position={tempMarkerPosition}
                                        draggable={true}
                                        onMarkerDragEnd={onSelectedMarkerDragEnd}
                                        zIndex={999}
                                    >
                                        <div className="w-5 h-5 rounded-full bg-red-600 border-2 border-white shadow-lg" />
                                    </GoogleMap.AdvancedMarker>
                                )}

                                <MapCameraMarker
                                    camera={camera}
                                    isSelected={isSelected}
                                    selectedAngle={isSelected ? cameraAngle : undefined}
                                    selectedRange={isSelected ? cameraRange : undefined}
                                    selectedFOV={isSelected ? cameraFOV : undefined}
                                    onMarkerClick={() => focusCameraOnMap(camera)}
                                />
                            </React.Fragment>
                        );
                    })}
                </GoogleMap>
            ) : (
                <div className="flex flex-col justify-center items-center h-full w-full">
                    <Lottie animationData={globeLottieAnimation} loop={true} className="w-[480px] -mt-20" />
                    <div className="text-sm text-grayscale-900 font-medium -mt-6">{loadingMessage}</div>
                </div>
            )}
        </>
    );
};

export default CamerasMapView;
